/**
 * ExecExplore — Full-page interactive data exploration.
 *
 * A single large chart with adjustment controls alongside it.
 * Users can change metric, grouping, chart type, comparison, and scope.
 * Supports group comparisons (overlaying data from other portfolios,
 * branches, or commands alongside the primary dataset).
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Compass, ArrowLeftRight, LayoutDashboard, Check, GitBranch } from 'lucide-react'
import { Link } from 'react-router-dom'
import { usePerformanceQuery } from '../components/exec/generic/usePerformanceQuery'
import { useBenchmarkQueries } from '../components/exec/generic/useBenchmarkQueries'
import BarChartRenderer from '../components/exec/generic/BarChartRenderer'
import LineChartRenderer from '../components/exec/generic/LineChartRenderer'
import BenchmarkPicker from '../components/exec/BenchmarkPicker'
import {
  METRIC_LABELS, DIMENSION_LABELS,
  type MetricKey, type GroupByDimension, type ChartType, type GenericWidgetConfig,
  DRILL_HIERARCHY,
} from '../components/exec/generic/types'
import { RefreshCw } from 'lucide-react'
import { execAPI } from '../services/exec'
import type { PortfolioGroup, BenchmarkSource } from '../types/exec'
import { trackEvent } from '../services/tracking'

const METRIC_OPTIONS: { value: MetricKey; label: string; group: string }[] = [
  { value: 'award_count', label: 'Award Count (actions)', group: 'Volume' },
  { value: 'unique_award_count', label: 'Distinct Contracts', group: 'Volume' },
  { value: 'total_obligated', label: 'Obligated Dollars', group: 'Volume' },
  { value: 'avg_days_to_award', label: 'Time to Contract', group: 'Speed' },
  { value: 'full_open_pct', label: 'Full & Open %', group: 'Competition' },
  { value: 'follow_on_pct', label: 'Follow-On %', group: 'Competition' },
  { value: 'ot_award_pct', label: 'Other Transaction %', group: 'Vehicles' },
  { value: 'deobligation_pct', label: 'Deobligation Rate', group: 'Volume' },
  { value: 'gross_obligated', label: 'Gross Obligated $', group: 'Volume' },
  { value: 'not_competed_pct', label: 'Not Competed %', group: 'Competition' },
  { value: 'single_offer_pct', label: 'Single Offer %', group: 'Competition' },
  { value: 'avg_offers_received', label: 'Avg Offers Received', group: 'Competition' },
  { value: 'sb_award_pct', label: 'SB Award %', group: 'Small Business' },
  { value: 'set_aside_pct', label: 'Set-Aside %', group: 'Small Business' },
  { value: 'count_8a', label: '8(a) Awards', group: 'Small Business' },
  { value: 'count_hubzone', label: 'HUBZone Awards', group: 'Small Business' },
  { value: 'count_sdvosb', label: 'SDVOSB Awards', group: 'Small Business' },
  { value: 'count_wosb', label: 'WOSB Awards', group: 'Small Business' },
  { value: 'ot_dollar_pct', label: 'OT Dollar %', group: 'Vehicles' },
]

const GROUP_BY_OPTIONS: { value: GroupByDimension; label: string }[] = [
  { value: 'fiscal_year', label: 'Fiscal Year' },
  { value: 'fiscal_quarter', label: 'Fiscal Quarter' },
  { value: 'sub_agency', label: 'Branch / Sub-Agency' },
  { value: 'macom', label: 'Major Command (MAJCOM)' },
  { value: 'subcom1', label: 'Sub-Command' },
  { value: 'awarding_office_code', label: 'Office' },
  { value: 'vehicle_category', label: 'Vehicle Category' },
  { value: 'psc_level1_code', label: 'PSC Category (L1)' },
  { value: 'psc_level2_code', label: 'PSC Category (L2)' },
  { value: 'color_of_money', label: 'Color of Money' },
  { value: 'extent_competed', label: 'Competition Status' },
  { value: 'is_ot', label: 'FAR vs OT' },
  { value: 'state', label: 'State (recipient HQ)' },
  { value: 'congressional_district', label: 'Congressional District' },
]

// Time dimensions render as line charts and stay chronological (never sorted by value).
const TIME_DIMENSIONS = new Set<GroupByDimension>(['fiscal_year', 'fiscal_quarter'])

// Metrics where a smaller value is better — sorted ascending so the chart and the
// Top-N limit lead with the best performers rather than the worst.
const LOWER_IS_BETTER = new Set<MetricKey>(['avg_days_to_award', 'not_competed_pct', 'single_offer_pct'])

// Org-hierarchy dimensions. Comparing one level by another is degenerate —
// each child rolls up to exactly one parent, so the grouped bars come out
// sparse; drill-down is the right tool for moving between org levels.
const ORG_HIERARCHY_DIMS = new Set<GroupByDimension>([
  'sub_agency', 'macom', 'subcom1', 'awarding_office_code',
])

/** A Compare By dimension is valid only if it isn't Group By itself and isn't
 *  another level of the same org hierarchy as Group By. */
function isValidCompareBy(groupBy: GroupByDimension, candidate: GroupByDimension): boolean {
  if (candidate === groupBy) return false
  if (ORG_HIERARCHY_DIMS.has(groupBy) && ORG_HIERARCHY_DIMS.has(candidate)) return false
  return true
}

// Metrics that are defined *by* a dimension — cross-tabbing them by that
// dimension is tautological (every cell collapses to 0% or 100%). Those
// dimensions are dropped from Group By / Compare By when the metric is active.
const METRIC_INCOMPATIBLE_DIMS: Partial<Record<MetricKey, Set<GroupByDimension>>> = {
  ot_award_pct: new Set<GroupByDimension>(['is_ot', 'vehicle_category']),
  ot_dollar_pct: new Set<GroupByDimension>(['is_ot', 'vehicle_category']),
  // Time to Contract comes from the SAM speed mart, which carries no vehicle
  // category and no recipient location — those dimensions aren't offered for it.
  avg_days_to_award: new Set<GroupByDimension>(['vehicle_category', 'state', 'congressional_district']),
  full_open_pct: new Set<GroupByDimension>(['extent_competed']),
  not_competed_pct: new Set<GroupByDimension>(['extent_competed']),
  single_offer_pct: new Set<GroupByDimension>(['extent_competed']),
}

/** Whether a dimension can be meaningfully cross-tabbed with a metric. */
function isDimCompatible(metric: MetricKey, dim: GroupByDimension): boolean {
  return !METRIC_INCOMPATIBLE_DIMS[metric]?.has(dim)
}

/**
 * Pick the chart automatically from the x-axis dimension — there is no manual
 * toggle. A time x-axis → line; everything else → bar. A categorical "Compare
 * By" just adds grouped series to the bars (or extra lines) — it never forces
 * a heatmap, which reads poorly for the sparse matrices these comparisons make.
 */
function deriveChartType(groupBy: GroupByDimension): ChartType {
  return TIME_DIMENSIONS.has(groupBy) ? 'line' : 'bar'
}

/**
 * Sort categorical charts by the metric value so bars are ranked. Time axes stay
 * chronological; 2-D (Compare By) views keep group order to avoid interleaving series.
 */
function deriveSortBy(
  groupBy: GroupByDimension,
  seriesBy: GroupByDimension | undefined,
  metric: MetricKey,
): string | undefined {
  if (seriesBy) return undefined
  if (TIME_DIMENSIONS.has(groupBy)) return undefined
  return LOWER_IS_BETTER.has(metric) ? metric : `-${metric}`
}

export default function ExecExplore() {
  const [metric, setMetric] = useState<MetricKey>('total_obligated')
  const [groupBy, setGroupBy] = useState<GroupByDimension>('sub_agency')
  const [seriesBy, setSeriesBy] = useState<GroupByDimension | ''>('')
  const [portfolioGroupId, setPortfolioGroupId] = useState('')
  const [portfolioGroups, setPortfolioGroups] = useState<PortfolioGroup[]>([])
  const [limit, setLimit] = useState(30)

  // Group comparisons (overlaid on top of primary data)
  const [groupComparisons, setGroupComparisons] = useState<BenchmarkSource[]>([])

  // Drill-down state
  const [drillStack, setDrillStack] = useState<{ dim: GroupByDimension; code: string; label: string }[]>([])

  useEffect(() => {
    execAPI.getPortfolioGroups().then(setPortfolioGroups).catch(() => {})
  }, [])

  const effectiveGroupBy = drillStack.length > 0
    ? drillStack[drillStack.length - 1].dim
    : groupBy

  // Drill mode drops the Compare By series.
  const effectiveSeriesBy = drillStack.length > 0 ? undefined : (seriesBy || undefined)

  // Chart type and sort order are derived from the dimensions — not user-chosen.
  const chartType = deriveChartType(effectiveGroupBy)

  // Group By / Compare By drop dimensions the current metric can't be
  // meaningfully cross-tabbed by; Compare By also excludes the current Group By
  // and its sibling org-hierarchy levels.
  const groupByOptions = GROUP_BY_OPTIONS.filter((o) => isDimCompatible(metric, o.value))
  const compareByOptions = GROUP_BY_OPTIONS.filter(
    (o) => isValidCompareBy(groupBy, o.value) && isDimCompatible(metric, o.value),
  )

  const config = useMemo((): GenericWidgetConfig => ({
    group_by: effectiveGroupBy,
    metrics: [metric],
    chart_type: chartType,
    series_by: effectiveSeriesBy as GroupByDimension | undefined,
    portfolio_group: portfolioGroupId || undefined,
    sort_by: deriveSortBy(effectiveGroupBy, effectiveSeriesBy, metric),
    limit,
  }), [effectiveGroupBy, effectiveSeriesBy, metric, chartType, portfolioGroupId, limit])

  const drillFilters = useMemo(() => {
    if (drillStack.length === 0) return undefined
    return { parent_code: drillStack[drillStack.length - 1].code }
  }, [drillStack])

  const { data, loading, refresh } = usePerformanceQuery(config, drillFilters)

  // Track explore queries — fires when data returns (confirms query executed)
  const queryTracked = useRef<string>('')
  useEffect(() => {
    if (loading || !data) return
    const key = JSON.stringify(config) + JSON.stringify(drillFilters ?? '')
    if (key === queryTracked.current) return
    queryTracked.current = key
    trackEvent('explore_query_executed', {
      metric,
      group_by: effectiveGroupBy,
      series_by: seriesBy || null,
      chart_type: chartType,
      portfolio_group_id: portfolioGroupId || null,
      limit,
      drill_depth: drillStack.length,
      result_count: data?.length ?? 0,
    })
  }, [loading, data, config, drillFilters, metric, effectiveGroupBy, seriesBy, chartType, portfolioGroupId, limit, drillStack.length])

  // Group comparison queries — always group by time dimension so data aligns.
  // When primary is time-grouped, comparisons align on the same X axis.
  // When primary is entity-grouped, comparisons aggregate to reference values.
  const comparisonBaseParams = useMemo(() => {
    const isTimeDim = (d: string) => d === 'fiscal_year' || d === 'fiscal_quarter'
    const params: Record<string, string | number> = {}

    if (isTimeDim(effectiveGroupBy)) {
      // Primary X-axis is time — comparisons group the same way
      params.group_by = effectiveGroupBy
    } else if (seriesBy && isTimeDim(seriesBy)) {
      // Primary X-axis is entities, but seriesBy is time — group comparisons by that time dim
      params.group_by = seriesBy
    } else {
      // No time dimension in play — group by fiscal_year as default
      params.group_by = 'fiscal_year'
    }
    if (limit) params.limit = limit
    return params
  }, [effectiveGroupBy, seriesBy, limit])

  const comparisonResult = useBenchmarkQueries(groupComparisons, comparisonBaseParams)

  const canDrill = !!DRILL_HIERARCHY[effectiveGroupBy]

  const handleDrillDown = (code: string, label: string) => {
    const next = DRILL_HIERARCHY[effectiveGroupBy]
    if (!next) return
    trackEvent('explore_drill_down', {
      from_dimension: effectiveGroupBy, to_dimension: next, code, label, metric,
    })
    setDrillStack((prev) => [...prev, { dim: next, code, label }])
  }

  const handleBreadcrumbClick = (index: number) => {
    if (index < 0) setDrillStack([])
    else setDrillStack((prev) => prev.slice(0, index + 1))
  }

  const primaryLabel = portfolioGroupId
    ? portfolioGroups.find((pg) => pg.id === portfolioGroupId)?.name || 'Portfolio'
    : 'All Offices'

  // Only pass comparisons to renderers when they have data
  const activeComparisons = comparisonResult.series.length > 0 ? comparisonResult.series : undefined

  // Swap group by and compare by
  const handleSwap = () => {
    if (!seriesBy) return
    const oldGroupBy = groupBy
    const oldSeriesBy = seriesBy
    setGroupBy(oldSeriesBy as GroupByDimension)
    setSeriesBy(oldGroupBy)
    setDrillStack([])
  }

  // Add current view to dashboard
  const [addingToDashboard, setAddingToDashboard] = useState(false)
  const [addedToDashboard, setAddedToDashboard] = useState(false)

  const handleAddToDashboard = useCallback(async () => {
    setAddingToDashboard(true)
    try {
      // Get or create default dashboard
      const dashboards = await execAPI.getDashboards()
      let dashboardId: string
      if (dashboards.length > 0) {
        // Use the most recently updated dashboard
        dashboardId = dashboards[0].id
      } else {
        const created = await execAPI.createDashboard({ name: 'My Dashboard', is_default: true })
        dashboardId = created.id
      }

      // Build widget config from current explore state. Chart type is derived
      // from the widget's own dimensions (base groupBy, not any drill state).
      const savedChartType = deriveChartType(groupBy)
      const widgetConfig: Record<string, unknown> = {
        group_by: groupBy,
        metrics: [metric],
        chart_type: savedChartType,
      }
      if (seriesBy) widgetConfig.series_by = seriesBy
      if (portfolioGroupId) widgetConfig.portfolio_group = portfolioGroupId
      if (limit !== 30) widgetConfig.limit = limit

      const title = `${METRIC_LABELS[metric]} by ${DIMENSION_LABELS[groupBy]}${
        seriesBy ? ` \u00d7 ${DIMENSION_LABELS[seriesBy as GroupByDimension]}` : ''
      }`

      await execAPI.addWidget(dashboardId, {
        measure: 'office_performance',
        config: widgetConfig,
        title,
      })

      // Update layout to place widget at the bottom
      const dash = await execAPI.getDashboard(dashboardId)
      const maxY = dash.layout.reduce((max: number, item: { y: number; h: number }) => Math.max(max, item.y + item.h), 0)
      const newWidget = dash.widgets[dash.widgets.length - 1]
      if (newWidget) {
        await execAPI.updateLayout(dashboardId, [
          ...dash.layout,
          { i: newWidget.id, x: 0, y: maxY, w: 6, h: 5 },
        ])
      }

      setAddedToDashboard(true)
      setTimeout(() => setAddedToDashboard(false), 2000)
    } catch (err) {
      console.error('Failed to add to dashboard:', err)
    } finally {
      setAddingToDashboard(false)
    }
  }, [groupBy, metric, seriesBy, portfolioGroupId, limit])

  const selectClass = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className="flex h-[calc(var(--app-height)-4rem)]">
      {/* Controls sidebar */}
      <div className="w-72 shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-y-auto p-4 space-y-5">
        <div className="flex items-center gap-2 mb-2">
          <Compass size={18} className="text-amber-500" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Explore</h2>
          <Link
            to="/exec/explore/flow"
            className="ml-auto text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground"
            title="Open Pathway Flow (AAF Contracting Cone parcat)"
          >
            <GitBranch size={12} /> Flow
          </Link>
        </div>

        {/* Metric */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Metric</label>
          <select
            value={metric}
            onChange={(e) => {
              const next = e.target.value as MetricKey
              setMetric(next)
              // Drop a Group By / Compare By the new metric can't be cross-tabbed by.
              if (!isDimCompatible(next, groupBy)) {
                const fallback = GROUP_BY_OPTIONS.find((o) => isDimCompatible(next, o.value))
                if (fallback) { setGroupBy(fallback.value); setDrillStack([]) }
              }
              if (seriesBy && !isDimCompatible(next, seriesBy)) setSeriesBy('')
            }}
            className={selectClass}
          >
            {METRIC_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.group}: {opt.label}</option>
            ))}
          </select>
        </div>

        {/* Group By */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Group By</label>
          <select
            value={groupBy}
            onChange={(e) => {
              const next = e.target.value as GroupByDimension
              setGroupBy(next)
              setDrillStack([])
              // Drop a Compare By that no longer cross-cuts the new Group By.
              if (seriesBy && !isValidCompareBy(next, seriesBy)) setSeriesBy('')
            }}
            className={selectClass}
          >
            {groupByOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Swap button */}
        {seriesBy && (
          <div className="flex justify-center -my-2">
            <button
              onClick={handleSwap}
              title="Swap Group By and Compare By"
              className="p-1 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 hover:text-blue-600 hover:border-blue-300 transition-colors"
            >
              <ArrowLeftRight size={14} />
            </button>
          </div>
        )}

        {/* Compare By */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Compare By</label>
          <select
            value={seriesBy}
            onChange={(e) => setSeriesBy(e.target.value as GroupByDimension | '')}
            className={selectClass}
          >
            <option value="">None</option>
            {compareByOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Scope */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Portfolio Scope</label>
          <select value={portfolioGroupId} onChange={(e) => setPortfolioGroupId(e.target.value)} className={selectClass}>
            <option value="">All offices</option>
            {portfolioGroups.map((pg) => (
              <option key={pg.id} value={pg.id}>{pg.name} ({pg.resolved_office_count})</option>
            ))}
          </select>
        </div>

        {/* Group Comparisons */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Group Comparisons</label>
          <p className="text-[10px] text-gray-400 mb-1.5">
            Overlay data from other portfolios, branches, or commands.
          </p>
          <BenchmarkPicker
            benchmarks={groupComparisons}
            onChange={setGroupComparisons}
            maxBenchmarks={5}
            excludePortfolioGroupId={portfolioGroupId}
          />
        </div>

        {/* Limit */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Max Results: {limit}</label>
          <input
            type="range"
            min={10}
            max={100}
            step={10}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="w-full"
          />
        </div>
      </div>

      {/* Chart area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Breadcrumbs + title */}
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <button onClick={() => handleBreadcrumbClick(-1)} className="hover:text-blue-600">
                {DIMENSION_LABELS[groupBy]}
              </button>
              {drillStack.map((level, i) => (
                <span key={i} className="flex items-center gap-1">
                  <span className="text-gray-300">/</span>
                  <button onClick={() => handleBreadcrumbClick(i)} className="hover:text-blue-600">
                    {level.label}
                  </button>
                </span>
              ))}
            </div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">
              {METRIC_LABELS[metric]} by {DIMENSION_LABELS[effectiveGroupBy]}
              {seriesBy && !drillStack.length ? `, compared by ${DIMENSION_LABELS[seriesBy as GroupByDimension]}` : ''}
              {groupComparisons.length > 0 ? ` + ${groupComparisons.length} group comparison${groupComparisons.length > 1 ? 's' : ''}` : ''}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              {data.length} result{data.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={handleAddToDashboard}
              disabled={addingToDashboard}
              title="Add to dashboard"
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                addedToDashboard
                  ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                  : 'text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              {addedToDashboard ? <Check size={12} /> : <LayoutDashboard size={12} />}
              {addingToDashboard ? 'Saving...' : addedToDashboard ? 'Added' : 'Add to Dashboard'}
            </button>
            <button
              onClick={refresh}
              title="Refresh data"
              className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        {/* Chart */}
        <div className="flex-1 min-h-0 relative p-2">
          {(loading || comparisonResult.allLoading) && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-gray-900/60 z-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          )}

          {chartType === 'line' ? (
            <LineChartRenderer data={data} groupBy={effectiveGroupBy} metrics={config.metrics} seriesBy={config.series_by} groupComparisons={activeComparisons} primaryLabel={primaryLabel} />
          ) : (
            <BarChartRenderer data={data} groupBy={effectiveGroupBy} metrics={config.metrics} seriesBy={config.series_by} onDrillDown={canDrill ? handleDrillDown : undefined} groupComparisons={activeComparisons} primaryLabel={primaryLabel} />
          )}
        </div>
      </div>
    </div>
  )
}
